import React, { useEffect, useState, useMemo } from "react";
import {
  Box, 
  Typography,
  TextField,
  Grid,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Alert,
  List,
  ListItem,
  Divider,
} from "@mui/material";
import * as Yup from "yup";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BasicModal from "../../components/BasicModal";
import FormModalActions from "../../components/FormModalActions";
import { LARGE_FORM_MODAL_SX } from "../../utils/formModalStyles";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useFormik } from "formik";
import { format } from "date-fns";
import {
  getDoctorByDepartmentId,
  getDoctorsByDepartmentIdMultiple,
  getDoctorById,
  getAllDoctors,
} from "../../services/doctorService";
import { getAllStaff } from "../../services/StaffService";
import { registerPatient, updatePatient, getAllPatients, syncPatientTypeForWorkflow } from "../../services/patientService";
import { saveAssignment } from "../../services/PatientAssignmentService";
import { create } from "../../services/PatientAdmissionService";
import dayjs from "dayjs";
import { getAllDepartment } from "../../services/departmentService";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getAllWards } from "../../services/WardService";
import { getBedsByWard, updateBed } from "../../services/BedService";
import { filterDepartmentsForIpdWorkflow } from "../../utils/subscriptionScopeUtils";
import { useSubscriptionCapabilities } from "../../hooks/useSubscriptionCapabilities";


const validationSchema = Yup.object({
  doctor_id: Yup.string().required("Assigning a doctor is required"),
  department_id: Yup.string().required("Department is required"),
  ward_id: Yup.string().required("Ward is required"),
  bed_id: Yup.string().required("Bed is required"),
  nurse_id: Yup.string().required("Nurse is required"),
  disease_diagnose:Yup.string().required("Disease Diagnose is required"),
  admission_type:Yup.string().required("Admission type is required"),
  admission_amount: Yup.string().required("Admission amount is required").matches(/^\d+$/, "Admission amount must be a number"),
});

const NewAdmissionModal = ({ open, onClose, patient_info = {}, refreshData }) => {
  const navigate = useNavigate();
  const now = new Date();
  const location = useLocation();
  const { hasCapability, isPremiumPackage } = useSubscriptionCapabilities();
  const hasIpdCapability = hasCapability("IPD") || hasCapability("In Patient");
  const canCreateInPatient = isPremiumPackage && hasIpdCapability;
  const [department_list, setDepartmentList] = useState([]);
  const [staff_list, setStaffList] = useState([]);
  const [filtered_nurses, setFilteredNurses] = useState([]);
  const [token_list, setTokenList] = useState([]);
  const [doctor_list, setDoctorList] = useState([]);
  const [doctor, setDoctor] = useState({});
  const [wardList, setWardList] = useState([]);
  const [bedList, setBedList] = useState([]);

  useEffect(() => {
    fetchDepartments();
    fetchInitialData();
  }, [patient_info]);

  const fetchDoctorsByDepartment = async (department_id: string) => {
    setDoctorList([]);
    try {
      // Get organizationId to validate doctors belong to this organization
      const storedUserForDoctor = localStorage.getItem("user");
      let organizationIdForDoctor: number | null = null;
      if (storedUserForDoctor) {
        try {
          const user = JSON.parse(storedUserForDoctor);
          const orgId = user?.organizationId;
          if (orgId != null && orgId !== "" && !isNaN(orgId) && Number(orgId) > 0) {
            organizationIdForDoctor = Number(orgId);
          }
        } catch (e) {
          console.error("Error parsing stored user:", e);
        }
      }
      
      // apiClient will automatically add organizationId as query param via interceptor
      // This ensures only organization-specific doctors are fetched
      console.log(`[InPatientAppointmentModal] Fetching doctors for department ${department_id} with organizationId: ${organizationIdForDoctor}`);
      console.log(`[InPatientAppointmentModal] Current user from localStorage:`, storedUserForDoctor ? JSON.parse(storedUserForDoctor) : "No user");
      
      const doctors = await getDoctorsByDepartmentIdMultiple(department_id);
      console.log(`[InPatientAppointmentModal] Raw API response - Total doctors: ${doctors?.length || 0}`);
      console.log("Fetched doctors with multiple departments:", doctors);
      
      if (!doctors || doctors.length === 0) {
        console.warn(`[InPatientAppointmentModal] ⚠️ No doctors returned from API for department ${department_id} with organizationId ${organizationIdForDoctor}`);
        console.warn(`[InPatientAppointmentModal] This could mean:`);
        console.warn(`  1. No doctors exist in organization ${organizationIdForDoctor} for this department`);
        console.warn(`  2. Backend is not filtering correctly by organizationId`);
        console.warn(`  3. Backend table ${organizationIdForDoctor}_doctors doesn't exist or is empty`);
        setDoctorList([]);
        return;
      }
      
      if (doctors && doctors.length > 0) {
        console.log("Sample doctor object structure:", JSON.stringify(doctors[0], null, 2));
        console.log("All doctor organizationIds:", doctors.map((d: any) => ({
          id: d.id,
          name: d.name,
          orgId: d.organizationId || d.organization_id || d.orgId || d.org_id || "NOT FOUND"
        })));
      }
      
      // Validate: Filter out any common data if user has organizationId
      let filteredDoctors: any[] = Array.isArray(doctors) ? doctors : [];
      if (organizationIdForDoctor && organizationIdForDoctor > 0) {
        // Check if any doctors have organizationId field
        const doctorsWithOrgId = filteredDoctors.filter((d: any) => {
          const orgId = d.organizationId || d.organization_id || d.orgId || d.org_id;
          return orgId != null && orgId !== "";
        });
        
        if (doctorsWithOrgId.length > 0) {
          // Backend includes organizationId in response - filter strictly
          console.log(`[InPatientAppointmentModal] Backend includes organizationId in response. Filtering strictly.`);
          const beforeFilter = filteredDoctors.length;
          filteredDoctors = filteredDoctors.filter((doctor: any) => {
            const doctorOrgId = doctor.organizationId || doctor.organization_id || doctor.orgId || doctor.org_id;
            if (doctorOrgId != null && doctorOrgId !== "") {
              const matches = Number(doctorOrgId) === organizationIdForDoctor;
              if (!matches) {
                console.log(`[InPatientAppointmentModal] ❌ Excluding doctor ${doctor.id} (${doctor.name}) - orgId: ${doctorOrgId}, expected: ${organizationIdForDoctor}`);
              }
              return matches;
            }
            return false;
          });
          console.log(`[InPatientAppointmentModal] ✅ Filtered to ${filteredDoctors.length} doctors for organization ${organizationIdForDoctor} (from ${beforeFilter} total)`);
        } else {
          // Backend doesn't include organizationId in response - trust backend filtering
          // Since we sent organizationId as query param, backend should have filtered correctly
          console.log(`[InPatientAppointmentModal] Backend doesn't include organizationId in response. Trusting backend filtering. Showing ${filteredDoctors.length} doctors.`);
          // Don't filter - trust the backend since we sent organizationId as query param
          // If backend is not filtering correctly, it will return all doctors from base table
          // In that case, we'll show them but log a warning
          if (filteredDoctors.length > 0) {
            console.warn(`[InPatientAppointmentModal] ⚠️ Backend returned ${filteredDoctors.length} doctors but none have organizationId field. Backend may not be filtering by organizationId correctly.`);
            console.warn(`[InPatientAppointmentModal] ⚠️ Showing all returned doctors. Please verify backend is filtering correctly.`);
          }
        }
      } else {
        // Common user: show all doctors (from base table)
        console.log(`[InPatientAppointmentModal] Fetched ${filteredDoctors.length} doctors (common user) for department ${department_id}`);
      }
      
      setDoctorList(filteredDoctors);
    } catch (error) {
      console.error("Error fetching doctors:", error);
      // Fallback to the old API if the new one fails
      try {
        const fallbackDoctors = await getDoctorByDepartmentId(department_id);
        console.log("Fallback doctors:", fallbackDoctors);
        
        // Apply same filtering for fallback
        const storedUserForFallback = localStorage.getItem("user");
        let organizationIdForFallback: number | null = null;
        if (storedUserForFallback) {
          try {
            const user = JSON.parse(storedUserForFallback);
            const orgId = user?.organizationId;
            if (orgId != null && orgId !== "" && !isNaN(orgId) && Number(orgId) > 0) {
              organizationIdForFallback = Number(orgId);
            }
          } catch (e) {
            console.error("Error parsing stored user:", e);
          }
        }
        
        let filteredDoctors: any[] = Array.isArray(fallbackDoctors) ? fallbackDoctors : [];
        if (organizationIdForFallback && organizationIdForFallback > 0) {
          // Check if any doctors have organizationId field
          const doctorsWithOrgId = filteredDoctors.filter((d: any) => {
            const orgId = d.organizationId || d.organization_id || d.orgId || d.org_id;
            return orgId != null && orgId !== "";
          });
          
          if (doctorsWithOrgId.length > 0) {
            // Backend includes organizationId - filter strictly
            filteredDoctors = filteredDoctors.filter((doctor: any) => {
              const doctorOrgId = doctor.organizationId || doctor.organization_id || doctor.orgId || doctor.org_id;
              if (doctorOrgId != null && doctorOrgId !== "") {
                return Number(doctorOrgId) === organizationIdForFallback;
              }
              return false;
            });
          } else {
            // Backend doesn't include organizationId - trust backend filtering
            console.log(`[InPatientAppointmentModal] Fallback: Trusting backend filtering. Showing ${filteredDoctors.length} doctors.`);
          }
        }
        
        setDoctorList(filteredDoctors);
      } catch (fallbackError) {
        console.error("Fallback also failed:", fallbackError);
        setDoctorList([]);
      }
    }
  };

  const fetchInitialData = async () => {
    try {
      const [wards, staff] = await Promise.all([
        getAllWards(),
        getAllStaff(),
      ]);
      setWardList(wards);
      setStaffList(staff);
    } catch (error) {
      console.error("Error loading initial data:", error);
    }
  };

  const fetchBedsForWard = async (wardId) => {
    try {
      // Re-fetch beds to ensure we have the latest status
      const beds = await getBedsByWard(wardId);
      
      // Strictly filter only unoccupied beds
      // This ensures we never show occupied beds for new admissions
      const availableBeds = beds.filter(bed => {
        // Only include beds that are explicitly not occupied
        // Handle both boolean and string values
        const isOccupied = bed.isOccupied === true || bed.isOccupied === "true" || bed.isOccupied === 1 || bed.isOccupied === "1";
        return !isOccupied;
      });
      
      setBedList(availableBeds);
    } catch (error) {
      console.error("Failed to fetch beds", error);
      setBedList([]);
    }
  };

  const fetchDoctorsById = async (doctor_id: string) => {
    setDoctor({});
    try {
      const data = await getDoctorById(doctor_id);
      setDoctor(data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchAllStaff();
  }, []);

  const fetchAllStaff = async () => {
    setDoctor({}); 
    try {
      const data = await getAllStaff();
      setStaffList(data);
      // Initialize filtered nurses with all staff
      setFilteredNurses(data.filter((user) => user.userRole.toLowerCase() === "staff"));
    } catch (error) {
      console.error("Failed to fetch staff:", error);
    }
  };

  const filterNursesByDepartment = (departmentId) => {
    if (!departmentId) {
      // If no department selected, show all staff
      setFilteredNurses(staff_list.filter((user) => user.userRole.toLowerCase() === "staff"));
    } else {
      // Filter staff by department
      const filtered = staff_list.filter(
        (user) => user.userRole.toLowerCase() === "staff" && user.departmentId === departmentId
      );
      console.log("Filtered nurses for department:", departmentId, filtered);
      setFilteredNurses(filtered);
    }
  };

  const fetchDepartments = async () => {
    setDepartmentList([]);
    try {
      // Departments are fetched from base table (common for all organizations)
      // getAllDepartment() uses axios directly (not apiClient) to avoid sending organizationId
      const data = await getAllDepartment();
      
      // Departments come from base table, so no filtering needed
      let filteredData: any[] = Array.isArray(data) ? data : [];
      console.log(`[InPatientAppointmentModal] Fetched ${filteredData.length} departments from base table`);
      
      setDepartmentList(filteredData);
    } catch (error) {
      console.error("Error fetching departments:", error);
      setDepartmentList([]);
    }
  };

  const formik = useFormik({
    initialValues: {
      doctor_id: "",
      department_id: "",
      ward_id: "",
      bed_id: "",
      nurse_id: "",
      disease_diagnose: "",
      admission_type: "",
      admission_amount: "",
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values) => { 
      try {
        if (!canCreateInPatient) {
          toast.error("In-patient admission is available only on the Premium package.", {
            position: "top-center",
            autoClose: 3000,
          });
          return;
        }
        // apiClient will automatically add organizationId via interceptor
        // This ensures admission is stored in organization-specific table (e.g., 4_ipd_admissions)
        const admissionData = {
          patientId:patient_info?.id,
          admissionDate: new Date().toISOString(),
          admissionTime: new Date().toTimeString().split(" ")[0],
          status: "admit",
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          admissionAmount: values.admission_amount,
          diseaseDiagnose: formik.values.disease_diagnose,
          admissionType: formik.values.admission_type,
        };
        const newAdmission = await create(admissionData);
        
        // Log for debugging
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          try {
            const user = JSON.parse(storedUser);
            const orgId = user?.organizationId;
            if (orgId != null && orgId !== "" && !isNaN(orgId) && Number(orgId) > 0) {
              console.log(`[InPatientAppointmentModal] Created admission for organization ${orgId}:`, newAdmission);
            }
          } catch (e) {
            // Ignore parse errors
          }
        }

        // Validate bed availability before saving assignment
        // Re-fetch beds to ensure we have the latest bed status
        try {
          const beds = await getBedsByWard(values.ward_id);
          const selectedBed = beds.find(bed => bed.id === values.bed_id);
          
          if (!selectedBed) {
            toast.error("Selected bed not found. Please select a different bed.", {
              position: "top-center",
              autoClose: 3000,
            });
            return;
          }
          
          // Check if bed is already occupied
          if (selectedBed.isOccupied === true) {
            toast.error("This bed is already occupied. Please select an available bed.", {
              position: "top-center",
              autoClose: 3000,
            });
            formik.setFieldError("bed_id", "This bed is already occupied");
            return;
          }
        } catch (bedCheckError) {
          console.error("Error checking bed availability:", bedCheckError);
          toast.error("Failed to verify bed availability. Please try again.", {
            position: "top-center",
            autoClose: 3000,
          });
          return;
        }
        
        // apiClient will automatically add organizationId via interceptor
        // This ensures assignment is stored in organization-specific table
        const assignmentData = {
          patientId: newAdmission.patientId,
          admissionId: newAdmission.id,
          wardId: values.ward_id,
          departmentId: values.department_id,
          bedId: values.bed_id,
          nurseId: values.nurse_id,
          doctorId: values.doctor_id,
          admissionDate: new Date().toISOString(),
        };
        const newAssignment = await saveAssignment(assignmentData);

        await syncPatientTypeForWorkflow(newAdmission.patientId || patient_info?.id, "IN");
        
        // Update bed status to occupied after successful assignment
        try {
          await updateBed(values.bed_id, { isOccupied: true });
          console.log(`[InPatientAppointmentModal] Bed ${values.bed_id} marked as occupied`);
        } catch (bedUpdateError) {
          console.error("Failed to update bed status:", bedUpdateError);
          // Don't block the flow, but log the error
          toast.warning("Bed assigned but status update failed. Please verify bed status manually.", {
            position: "top-center",
            autoClose: 3000,
          });
        }
        
        // Log for debugging
        if (storedUser) {
          try {
            const user = JSON.parse(storedUser);
            const orgId = user?.organizationId;
            if (orgId != null && orgId !== "" && !isNaN(orgId) && Number(orgId) > 0) {
              console.log(`[InPatientAppointmentModal] Created assignment for organization ${orgId}:`, newAssignment);
            }
          } catch (e) {
            // Ignore parse errors
          }
        }
        alert(" Patient re-admitted successfully ");
        toast.success("Patient re-admitted successfully", {
          position: "top-center",
          autoClose: 2000,
        });

        // ✅ Refresh parent admission data
        if (typeof refreshData === "function") {
          refreshData();
        }

        // ✅ Close modal
        if (typeof onClose === "function") {
          onClose();
        }

      formik.resetForm();
      setDoctorList([]);

      } catch (error) {
        toast.error(error?.response?.data || "Failed to save assignment and admission.", {
          position: "bottom-center",
          autoClose: 3000,
        });
        console.error(error);
      }
    }
  });

  const handelCloseModalState = async () => {
    if (location.pathname === "/manage-patient") {
      navigate("/manage-patient");
    }
    if (location.pathname === "/path-patient") {
      navigate("/path-patient");
    }
    if (location.pathname === "/front-desk") {
      navigate("/front-desk");
    }
    await onClose();
    formik.resetForm();
    setDoctorList([]);
  };

  const getCurrentTime = () => {
    return dayjs().format("HH:mm");
  };

  const visibleDepartments = useMemo(
    () =>
      filterDepartmentsForIpdWorkflow(department_list, {
        keepIds: [formik.values.department_id],
      }),
    [department_list, formik.values.department_id]
  );

  return (
    <BasicModal
      open={open}
      onClose={handelCloseModalState}
      sx={LARGE_FORM_MODAL_SX}
      title="Create New Admission"
    >
      <Box display={"flex"} flexDirection={"column"}>
        <>
          <Box>
            <form onSubmit={formik.handleSubmit}>
              <Grid container spacing={2}>
                <>
                  <Grid item xs={6}>
                    <TextField
                      id="department_id"
                      name="department_id"
                      fullWidth
                      select
                      label="Department"
                      value={formik.values.department_id}
                      onChange={async (e) => {
                        formik.setFieldValue("department_id", parseInt(e.target.value));
                        formik.setFieldValue("doctor_id", "");
                        formik.setFieldValue("ward_id", "");
                        formik.setFieldValue("bed_id", "");
                        formik.setFieldValue("nurse_id", "");
                        setDoctor({});
                        await fetchDoctorsByDepartment(e.target.value);
                        filterNursesByDepartment(parseInt(e.target.value)); // Filter nurses by department
                        formik.validateField("department_id");
                      }}
                      error={formik.touched.department_id && Boolean(formik.errors.department_id)}
                      helperText={formik.touched.department_id && formik.errors.department_id}
                    >
                      {visibleDepartments.map((d) => (
                        <MenuItem key={d.id} value={d.id}>
                          {d.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      select
                      fullWidth
                      name="doctor_id"
                      label="Assign Doctor"
                      value={formik.values.doctor_id}
                      onChange={async (e) => {
                        formik.setFieldValue("doctor_id", e.target.value);
                        await fetchDoctorsById(e.target.value);
                        formik.validateField("doctor_id");
                      }}
                      onBlur={formik.handleBlur}
                      error={formik.touched.doctor_id && Boolean(formik.errors.doctor_id)}
                      helperText={
                        doctor_list.length === 0
                          ? "No doctors available for this department"
                          : formik.touched.doctor_id && formik.errors.doctor_id
                      }
                      disabled={doctor_list.length === 0}
                          SelectProps={{
                            MenuProps: {
                              PaperProps: {
                                style: {
                                  maxHeight: 200,
                                  overflowY: "auto",
                                },
                              },
                              anchorOrigin: {
                                vertical: "bottom",
                                horizontal: "left",
                              },
                              transformOrigin: {
                                vertical: "top",
                                horizontal: "left",
                              },
                            },
                          }}
                    >
                    {(doctor_list || []).map((doctor) => (
                      <MenuItem key={doctor.id} value={doctor.id}>
                        {doctor.name}
                      </MenuItem>
                    ))} 
                    </TextField>
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      select
                      fullWidth
                      name="ward_id"
                      label="Ward"
                      value={formik.values.ward_id}
                      onChange={(e) => {
                        const ward_id = e.target.value;
                        formik.setFieldValue("ward_id", ward_id);
                        formik.setFieldTouched("ward_id", true);
                        fetchBedsForWard(ward_id);
                        formik.setFieldValue("bed_id", "");
                      }}
                      onBlur={formik.handleBlur}
                      error={formik.touched.ward_id && Boolean(formik.errors.ward_id)}
                      helperText={formik.touched.ward_id && formik.errors.ward_id}
                    >
                      {wardList.map((ward) => (
                        <MenuItem key={ward.id} value={ward.id}>
                          {ward.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      select
                      fullWidth
                      name="bed_id"
                      label="Bed"
                      value={formik.values.bed_id}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.bed_id && Boolean(formik.errors.bed_id)}
                      helperText={formik.touched.bed_id && formik.errors.bed_id}
                    >
                      {bedList.length === 0 ? (
                        <MenuItem disabled>No beds available</MenuItem>
                      ) : (
                        bedList.map((bed) => (
                          <MenuItem key={bed.id} value={bed.id}>
                            {bed.bedNumber}
                          </MenuItem>
                        ))
                      )}
                    </TextField>
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      id="disease_diagnose"
                      name="disease_diagnose"
                      label="Disease Diagnosed"
                      value={formik.values.disease_diagnose}
                      onChange={formik.handleChange}
                      error={formik.touched.disease_diagnose && Boolean(formik.errors.disease_diagnose)}
                      helperText={formik.touched.disease_diagnose && formik.errors.disease_diagnose}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      id="nurse_id"
                      name="nurse_id"
                      fullWidth
                      select
                      label="Assign Nurse"
                      value={formik.values.nurse_id}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.nurse_id && Boolean(formik.errors.nurse_id)}
                      helperText={formik.touched.nurse_id && formik.errors.nurse_id}
                    >
                      {filtered_nurses.length === 0 ? (
                        <MenuItem disabled>No nurses available for this department</MenuItem>
                      ) : (
                        filtered_nurses.map((nurse) => (
                          <MenuItem key={nurse.id} value={nurse.id}>
                            {nurse.name}
                          </MenuItem>
                        ))
                      )}
                    </TextField>
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      id="admission_type"
                      name="admission_type"
                      fullWidth
                      select
                      label="Urgency Type"
                      value={formik.values.admission_type}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.admission_type && Boolean(formik.errors.admission_type)}
                      helperText={formik.touched.admission_type && formik.errors.admission_type}
                    >
                      <MenuItem value="Scheduled">Scheduled</MenuItem>
                      <MenuItem value="Urgency">Urgency</MenuItem>
                      <MenuItem value="Emergency">Emergency</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      id="admission_amount"
                      name="admission_amount"
                      label="Admission Amount"
                      value={formik.values.admission_amount}
                      onChange={formik.handleChange}
                      error={formik.touched.admission_amount && Boolean(formik.errors.admission_amount)}
                      helperText={formik.touched.admission_amount && formik.errors.admission_amount}
                    />
                  </Grid>
                </>

                <Grid item xs={12}>
                  <FormModalActions
                    onCancel={handelCloseModalState}
                    submitType="submit"
                    loading={formik.isSubmitting}
                  />
                </Grid>
              </Grid>
            </form>
          </Box>
        </>
      </Box>
    </BasicModal>
  );
};

export default NewAdmissionModal;